import { useTranslation } from '@thoughtspot/i18n';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { getCurrentOrgId, isAdminRoutingEnabled, isOrgUiEnabled, } from '@thoughtspot/session-service';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState, } from 'react';
import { useGetSidebarConfigCacheFirst, useGetStatusLazy, } from '../../../services/sidebar/sidebar-service';
import { getUserManagementVisibility } from '../../../utils/sidebar-config';
import { AdminPageHeader } from '../admin-page-header/admin-page-header';
import { UserManagementTranslationKeys } from '../constants';
import { UserManagementStatusProvider } from './shared';
import { PRIMARY_ORG_VIEW_MODE, resolveAdminOrgContext, resolveEffectiveAdminOrgId, } from './shared/admin-org-context';
import UsersContainer from './users/users.container';
const GroupsContainer = lazy(() => import('./groups/groups.container'));
const RolesContainer = lazy(() => import('./roles/roles.container'));
const AuthenticationContainer = lazy(() => import('./authentication/authentication.container'));
export var UserManagementTab;
(function (UserManagementTab) {
    UserManagementTab["Users"] = "users";
    UserManagementTab["Groups"] = "groups";
    UserManagementTab["Roles"] = "roles";
    UserManagementTab["Authentication"] = "authentication";
})(UserManagementTab || (UserManagementTab = {}));
const TAB_COMPONENTS = {
    [UserManagementTab.Users]: UsersContainer,
    [UserManagementTab.Groups]: GroupsContainer,
    [UserManagementTab.Roles]: RolesContainer,
    [UserManagementTab.Authentication]: AuthenticationContainer,
};
export const UserManagementContainer = ({ currentOrgId, orgViewModeType, }) => {
    const sessionOrgId = getCurrentOrgId();
    const effectiveCurrentOrgId = useMemo(() => resolveEffectiveAdminOrgId({
        currentOrgId,
        sessionOrgId,
        orgUiEnabled: isOrgUiEnabled(),
        orgViewModeType,
    }), [currentOrgId, orgViewModeType, sessionOrgId]);
    const { isAllOrgContext } = useMemo(() => resolveAdminOrgContext({
        effectiveCurrentOrgId,
        orgUiEnabled: isOrgUiEnabled(),
        orgViewModeType,
    }), [effectiveCurrentOrgId, orgViewModeType]);
    const [tabOrgId, setTabOrgId] = useState(effectiveCurrentOrgId);
    useEffect(() => {
        const id = setTimeout(() => setTabOrgId(effectiveCurrentOrgId), 0);
        return () => clearTimeout(id);
    }, [effectiveCurrentOrgId]);
    const viewMode = useMemo(() => {
        if (!isOrgUiEnabled())
            return '';
        if (isAllOrgContext)
            return 'all';
        if (orgViewModeType === PRIMARY_ORG_VIEW_MODE)
            return 'primary';
        if (orgViewModeType)
            return '';
        if (sessionOrgId > 0)
            return '';
        return 'primary';
    }, [isAllOrgContext, orgViewModeType, sessionOrgId]);
    const { data: sidebarConfig, error: sidebarError, getSidebarConfig, } = useGetSidebarConfigCacheFirst();
    useEffect(() => {
        getSidebarConfig({ variables: { viewMode } });
    }, [viewMode, getSidebarConfig]);
    const { data: statusData, error: statusError, getStatus, } = useGetStatusLazy();
    useEffect(() => {
        getStatus({ variables: { viewMode, orgsEnabled: false } });
    }, [viewMode, getStatus]);
    const statusConfig = statusData?.Data;
    const visibility = useMemo(() => getUserManagementVisibility(sidebarConfig), [sidebarConfig]);
    const sidebarSettled = sidebarConfig !== undefined || !!sidebarError;
    const statusSettled = statusData !== undefined || !!statusError;
    const getInitialTab = () => {
        if (isAdminRoutingEnabled()) {
            const hash = window.location.hash || '';
            const match = hash.match(/#\/admin\/(?:all-orgs\/)?user-management\/(\w+)/);
            if (match) {
                const subTab = match[1];
                if (subTab in TAB_COMPONENTS) {
                    return subTab;
                }
            }
        }
        return UserManagementTab.Users;
    };
    const [currentTab, setCurrentTab] = useState(getInitialTab);
    const buildSubTabUrl = useCallback((tab) => {
        const prefix = isAllOrgContext
            ? '#/admin/all-orgs/user-management'
            : '#/admin/user-management';
        return `${prefix}/${tab}`;
    }, [isAllOrgContext]);
    const syncSubTabToUrl = useCallback((tab) => {
        if (!isAdminRoutingEnabled())
            return;
        window.history.replaceState(null, '', buildSubTabUrl(tab));
    }, [buildSubTabUrl]);
    useEffect(() => {
        const id = setTimeout(() => syncSubTabToUrl(currentTab), 0);
        return () => clearTimeout(id);
    }, [syncSubTabToUrl, currentTab]);
    const handleTabChange = useCallback((tab) => {
        if (tab in TAB_COMPONENTS) {
            setCurrentTab(tab);
            syncSubTabToUrl(tab);
        }
    }, [syncSubTabToUrl]);
    const { t } = useTranslation();
    const ActiveTabComponent = currentTab === UserManagementTab.Authentication
        ? null
        : TAB_COMPONENTS[currentTab];
    const authTabContent = statusSettled ? (React.createElement(AuthenticationContainer, { key: `${currentTab}-${effectiveCurrentOrgId}-${orgViewModeType ||
            ''}`, currentOrgId: effectiveCurrentOrgId, orgViewModeType: orgViewModeType })) : (React.createElement(LoadingIndicator, null,
        React.createElement(React.Fragment, null)));
    const tabs = useMemo(() => [
        {
            id: UserManagementTab.Users,
            title: t(UserManagementTranslationKeys.TAB_USERS),
            trackId: 'user-management-users',
            show: visibility?.users,
        },
        {
            id: UserManagementTab.Groups,
            title: t(UserManagementTranslationKeys.TAB_GROUPS),
            trackId: 'user-management-groups',
            show: visibility?.groups,
        },
        {
            id: UserManagementTab.Roles,
            title: t(UserManagementTranslationKeys.TAB_ROLES),
            trackId: 'user-management-roles',
            show: visibility?.roles,
        },
        {
            id: UserManagementTab.Authentication,
            title: t(UserManagementTranslationKeys.TAB_AUTHENTICATION),
            trackId: 'user-management-authentication',
            show: visibility?.authentication,
        },
    ], [t, visibility]);
    const visibleTabs = useMemo(() => tabs.filter(tab => tab.show === true), [
        tabs,
    ]);
    useEffect(() => {
        if (sidebarSettled &&
            visibleTabs.length > 0 &&
            !visibleTabs.some(tab => tab.id === currentTab)) {
            setCurrentTab(visibleTabs[0].id);
        }
    }, [sidebarSettled, currentTab, visibleTabs]);
    return (React.createElement(UserManagementStatusProvider, { statusConfig: statusConfig ?? null, isAllOrgContext: isAllOrgContext, effectiveOrgId: effectiveCurrentOrgId },
        React.createElement(Vertical, { grow: true, minHeight: 0 },
            React.createElement(AdminPageHeader, { title: t(UserManagementTranslationKeys.TITLE), tabs: visibleTabs, selectedTabId: currentTab, onTabChange: id => handleTabChange(id), titleTestId: "user-management-title", tabTestIdPrefix: "user-management-tab" }),
            sidebarSettled && (React.createElement(Suspense, { fallback: React.createElement(LoadingIndicator, null,
                    React.createElement(React.Fragment, null)) }, currentTab === UserManagementTab.Authentication
                ?
                    authTabContent
                : ActiveTabComponent && (React.createElement(ActiveTabComponent, { key: `${currentTab}-${tabOrgId}`, currentOrgId: tabOrgId })))))));
};
//# sourceMappingURL=user-management.container.js.map